import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { CouponSubType } from '@cds/betting-offer/domain-specific/coupons';
import { FixtureFormMode, SortByCriteria } from '@cds/query-objects';
import { ScrollingSizeConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { CouponTypes } from '@frontend/sports/coupons/feature/models';
import { GridEvent, GridGrouping, GridSorting } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid } from '@frontend/sports/grid/core/feature/observables';
import { GridActions, IGridAddCouponEvent, IGridRootState, getEvents } from '@frontend/sports/grid/core/feature/store';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash-es';
import { finalize } from 'rxjs';

import { HideHeaderService } from '../../common/hide-header.service';
import { ObservableGridFactoryWithCoupons } from './coupon-observable-grid.factory';
import { CouponDetail, CouponType } from './coupon.model';
import { CouponService } from './coupon.service';

@HooksWireup()
@Component({
    selector: 'ms-coupon',
    templateUrl: 'coupon.html',
    providers: [ObservableGridFactoryWithCoupons],
})
export class CouponComponent extends OnRouteResolve<CouponDetail> implements OnInit, OnDestroy {
    @HostBinding('class') className = 'coupons-list';

    private take: number;
    coupon: CouponDetail;
    grid$: ObservableGrid | undefined;
    loading = false;
    isFormCoupon = false;
    form?: FixtureFormMode;
    couponType: CouponTypes;
    private page = RouteTag.Coupons;

    constructor(
        private gridFactory: ObservableGridFactoryWithCoupons,
        private couponService: CouponService,
        public sitecore: Sitecore,
        route: ActivatedRoute,
        private changeDetectorRef: ChangeDetectorRef,
        private store: Store<IGridRootState>,
        private scrollContainer: ScrollContainerService,
        private hideHeaderService: HideHeaderService,
        private scrollingSizeConfig: ScrollingSizeConfig,
    ) {
        super(route);
        this.take = 10;
    }

    onFormSwitch(form: FixtureFormMode): void {
        this.form = form;
        this.getFormFixtures(form, false);
    }

    ngOnInit(): void {
        if (this.scrollingSizeConfig.hideHeaderOnScroll.includes(this.page)) {
            this.scrollContainer.scroll.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                this.hideHeaderService.onScroll();
            });
        }
    }

    onRouteResolved(model: CouponDetail): void {
        this.form = FixtureFormMode.Overall;

        if (!model) {
            return;
        }

        let sorting;

        if (model.page.totalCompetitions && model.page.totalCompetitions > 1) {
            sorting = model.sorting;
        }

        this.coupon = model;
        this.isFormCoupon = model.type === CouponType.FormCoupon;
        this.couponType = CouponTypes[this.coupon.couponType! as keyof typeof CouponTypes];
        this.grid$ = this.gridFactory.fromCouponResponse(
            `${model.sport.id}/coupons/${model.id}`,
            model.page.fixtures,
            {
                sorting: model.type === CouponType.FormCoupon ? undefined : sorting,
                grouping: this.mapGrouping(),
                groupOrder: this.coupon.gridGroups,
                marketGrouping: !!this.couponType,
            },
            false,
            this.couponType,
        );
    }

    loadMore(): void {
        if (!this.hasMore() || this.loading) {
            return;
        }

        this.getFixtures();
    }

    hasMore(): boolean {
        return this.coupon && this.coupon.page && this.coupon.page.totalCount > this.getFixtureCount();
    }

    getTracking(): string {
        let tracking = 'CouponView/';
        if (this.coupon.type === CouponType.FormCoupon) {
            tracking += 'FormCoupon';
        } else {
            tracking += 'Coupon';
        }

        return tracking;
    }

    ngOnDestroy(): void {
        this.grid$?.destroy();
    }

    private getFixtureCount(): number {
        if (!this.grid$) {
            return 0;
        }

        return getEvents(this.grid$.getState()).length;
    }

    private getFixtures(): void {
        if (this.coupon.type === CouponType.FormCoupon) {
            this.getFormFixtures(this.form || FixtureFormMode.Overall, true);

            return;
        }

        this.loading = true;

        const sorting = this.grid$ && this.grid$.getState().sorting === GridSorting.Competition ? SortByCriteria.Tags : SortByCriteria.StartDate;

        this.couponService
            .getCoupon(this.coupon.sport.id.toString(), this.coupon.id, CouponSubType.Regular, this.getFixtureCount(), this.take, sorting)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe((coupon) => {
                const gridId = this.grid$?.getState()?.id;
                if (coupon?.fixturePage.fixtures && gridId) {
                    const couponEvents: IGridAddCouponEvent = {
                        events: coupon.fixturePage.fixtures,
                        couponType: this.couponType,
                        id: gridId,
                    };
                    this.store.dispatch(GridActions.addCouponEvents({ payload: couponEvents }));
                }
            });
    }

    private getFormFixtures(form: FixtureFormMode, scrolling?: boolean): void {
        this.loading = !!scrolling;

        this.couponService
            .getCoupon(
                this.coupon.sport.id.toString(),
                this.coupon.id,
                CouponSubType.Regular,
                scrolling ? this.getFixtureCount() : 0,
                this.take,
                SortByCriteria.FixtureForm,
                form,
            )
            .pipe(finalize(() => (this.loading = false)))
            .subscribe((coupon) => {
                if (!coupon?.fixturePage.fixtures) {
                    return;
                }
                if (scrolling) {
                    this.grid$?.addEvents(coupon.fixturePage.fixtures);
                } else {
                    if (isEmpty(coupon.fixturePage.fixtures)) {
                        this.grid$?.removeEvents(
                            this.grid$
                                .getState()
                                .groups.reduce((resultEvents: GridEvent[], group) => {
                                    return resultEvents.concat(group.events);
                                }, [])
                                .map((event) => event.id),
                        );

                        return;
                    }
                    this.grid$?.destroy();
                    this.grid$ = this.gridFactory.fromResponse(`${this.coupon.sport.id}/coupons/${this.coupon.id}`, coupon.fixturePage.fixtures, {
                        grouping: this.mapGrouping(),
                        groupOrder: this.coupon.gridGroups,
                    });
                    this.changeDetectorRef.detectChanges();
                }
            });
    }

    private mapGrouping(): GridGrouping {
        const grouping = {
            [GridSorting.Time]: GridGrouping.Date,
            [FixtureFormMode.Home]: GridGrouping.HomeForm,
            [FixtureFormMode.Away]: GridGrouping.AwayForm,
            [FixtureFormMode.Overall]: GridGrouping.OverallForm,
        };

        if (this.coupon.type === CouponType.FormCoupon) {
            return grouping[this.form! as keyof typeof grouping] || GridGrouping.OverallForm;
        }

        return grouping[this.coupon.sorting! as keyof typeof grouping] || GridGrouping.League;
    }
}
